/**
 * TemplateEditor — the controlled create/edit/fork/view form for one template,
 * rendered as its own page (header bar + fields row + steps, and the preview
 * as a right-hand panel) rather than an inline panel. Layout follows Figma
 * "Templates — Edit template" (App Admin, node 554:46177). `view` is the same
 * page read-only, for a global template or a viewer without write.
 *
 * Preview rule: the BE renders a SAVED row, so the pane only ever previews
 * what was last saved. In create/fork mode there is nothing to preview until
 * the first save; in edit mode a dirty form says so rather than passing stale
 * words off as current. Nothing here renders the draft through a second code
 * path — the one that will send it is the only one that renders it.
 *
 * Validation mirrors the BE's ValidateSteps so the reason shows before the
 * round trip; INVALID_STEPS / UNKNOWN_MERGE_TAG coming back from the server
 * still render inline underneath.
 *
 * A fork is always a campaign template — the BE rejects scope=brand with a
 * parent (INVALID_TEMPLATE_SCOPE) — so the scope picker only ever shows for
 * a fresh create, never a fork.
 */
import { Fragment, useEffect, useId, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import type {
  OutreachFillStepRequest,
  OutreachFillStepResult,
  OutreachTemplate,
  OutreachTemplateInput,
  OutreachTemplateKind,
  OutreachTemplateLintWarning,
  OutreachTemplatePatch,
  OutreachTemplatePreview,
  OutreachTemplateScope,
} from '@discovr/types';
import { ArrowLeft, Check, Copy, Info, Plus } from '../../../design-system/icons';
import gmailLogo from '../../../assets/connectors/gmail.png';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { SignatureEditor, TEMPLATE_SIGNATURE_HELP } from './SignatureEditor';
import { KIND_LABELS } from './TemplateCard';
import { lintLabel } from './templateLintLabels';
import {
  SIGN_OFF_KEPT_FOR_FORMATTING,
  SIGN_OFF_WARNING,
  SIGNATURE_WARNING_CODES,
  SUBJECT_SHORT_WARNING,
  isShortSubject,
  signOffLineIndex,
  signOffText,
  signatureWarnings,
  stripSignOff,
} from './templateLint';
import { TemplateStepEditor, type TemplateStepField } from './TemplateStepEditor';
import { stripSignOffHtml } from './signOffHtml';
import type { ComposerEditorHandle } from './composerEditor/ComposerEditor';
import { canonicalHtml, hasFormatting } from './composerEditor/stripHtmlAttributes';
import { composerHtmlFromText, composerTextFromHtml } from './composerEditor/headless';
import { bodyOverCap, templateSubjectOverCap } from './draftCaps';
import { subjectCouldBeLongWarning, subjectEstimateOverCap, type SubjectEstimateContext } from './subjectEstimate';
import { TemplatePreviewPane, type TemplatePreviewEntry } from './TemplatePreviewPane';
import { apiErrorCopy, codeOf } from './sendLabels';
import { PriorStepsEmptyError } from './templateFillError';
import {
  FOLLOW_UP_CAP_NOTE,
  MAX_STEPS,
  defaultTemplateName,
  emptyFirstStep,
  emptyFollowUpStep,
  toStepDrafts,
  toTemplateSteps,
  validateTemplateDraft,
  type TemplateStepDraft,
} from './templateValidation';

export type TemplateEditorMode = 'create' | 'edit' | 'fork' | 'view';

export type TemplateEditorScope = Extract<OutreachTemplateScope, 'campaign' | 'brand'>;

const SELECT_CLASS =
  'h-9 w-full rounded-md border border-border bg-background px-3 text-body-sm-regular disabled:opacity-50';

export interface TemplateEditorProps {
  mode: TemplateEditorMode;
  /** The edit target, or the fork source. */
  initial?: OutreachTemplate;
  /** Create only — a fork is always a campaign template (BE spec §3). */
  scopeOptions: TemplateEditorScope[];
  /** "Used for" options — create/fork only; editing locks it (2.3's patch has no kind). */
  kindOptions?: OutreachTemplateKind[];
  entries: TemplatePreviewEntry[];
  onPreview: (templateId: number, entryId: string) => Promise<OutreachTemplatePreview>;
  onSave: (input: OutreachTemplateInput | OutreachTemplatePatch, confirmBrand: boolean) => Promise<void>;
  onCancel: () => void;
  /** View mode only: forks the shown template into this campaign. Absent when the viewer can't write. */
  onDuplicate?: () => void;
  /** Asks the AI to write one step (the fill-step route); rejects when it fails. Absent: the AI button stays off. */
  onFillStep?: (req: OutreachFillStepRequest) => Promise<OutreachFillStepResult>;
  /** The last save's advisory lint; shown only while the form still matches that save. */
  lintWarnings?: OutreachTemplateLintWarning[];
  /** The campaign's real names, so step 1's subject can be estimated once its merge fields are filled in. */
  subjectContext?: Omit<SubjectEstimateContext, 'year'>;
}

function subjectWarningFor(subject: string, context: TemplateEditorProps['subjectContext']): string | undefined {
  const estimate = context ? subjectEstimateOverCap(subject, { ...context, year: new Date().getFullYear() }) : null;
  if (estimate !== null) return subjectCouldBeLongWarning(estimate);
  return isShortSubject(subject) ? SUBJECT_SHORT_WARNING : undefined;
}

function initialName(mode: TemplateEditorMode, initial?: OutreachTemplate): string {
  if (!initial) return '';
  return mode === 'fork' ? `${initial.name} (this campaign)`.slice(0, 120) : initial.name;
}

function initialSteps(initial?: OutreachTemplate): TemplateStepDraft[] {
  if (!initial || initial.steps.length === 0) return [emptyFirstStep()];
  return toStepDrafts(initial.steps);
}

export const EDGE_REFUSAL_COPY =
  'The save was blocked before it reached Discovr — remove HTML-like text such as <script> and try again.';

/** Refused at the edge (WAF) rather than by the app: the browser sees status 0
 *  (the edge's 403 has no CORS headers), or a 403 without the app's JSON error
 *  body, which apiClient codes UNKNOWN (E2E I6). */
export function isEdgeRefusal(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false;
  const { httpStatus, code } = err as { httpStatus?: unknown; code?: unknown };
  return httpStatus === 0 || (httpStatus === 403 && code === 'UNKNOWN');
}

function saveErrorCopy(err: unknown): string {
  if (isEdgeRefusal(err)) return EDGE_REFUSAL_COPY;
  return apiErrorCopy(err, 'Could not save this template.', 'template');
}

function sameSteps(a: TemplateStepDraft[], b: TemplateStepDraft[]): boolean {
  return (
    a.length === b.length &&
    a.every((step, i) => {
      const other = b[i];
      return (
        !!other &&
        step.delayDays === other.delayDays &&
        step.subject === other.subject &&
        step.body === other.body &&
        canonicalHtml(step.bodyHtml) === canonicalHtml(other.bodyHtml)
      );
    })
  );
}

const MAX_DELAY_DAYS = 365;
const NAME_TAKEN = 'TEMPLATE_NAME_TAKEN';

/** A step's formatted body as the AI route wants it: the editor's HTML, or the plain text wrapped the same way. */
const bodyHtmlOf = (step: TemplateStepDraft): string => step.bodyHtml || composerHtmlFromText(step.body);

const TEMPLATE_HELP =
  "The agent writes each creator's email from this template and the campaign brief. Kept as written: the ask, figures, links. Reworded: the body. Subject: tidied once by AI for the whole campaign; tags like {creator_name} fill in per creator.";

export function TemplateEditor({
  mode,
  initial,
  scopeOptions,
  kindOptions,
  entries,
  onPreview,
  onSave,
  onCancel,
  onDuplicate,
  onFillStep,
  lintWarnings,
  subjectContext,
}: TemplateEditorProps) {
  const uid = useId();
  const readOnly = mode === 'view';
  const nameId = `${uid}-name`;
  const nameErrorId = `${uid}-name-error`;
  const channelId = `${uid}-channel`;
  const kindId = `${uid}-kind`;
  const scopeId = `${uid}-scope`;

  const baseline = useMemo(
    () => ({ name: initialName(mode, initial), steps: initialSteps(initial), signature: initial?.signature ?? '' }),
    [mode, initial],
  );

  const [typedName, setTypedName] = useState<string | null>(null);
  const [archivedNos, setArchivedNos] = useState<number[]>([]);
  const [scope, setScope] = useState<TemplateEditorScope>(() => scopeOptions[0] ?? 'campaign');
  const [kind, setKind] = useState<OutreachTemplateKind>(initial?.kind ?? 'sequence');
  const [steps, setSteps] = useState<TemplateStepDraft[]>(baseline.steps);
  const [signature, setSignature] = useState(baseline.signature);
  // null until the signature is edited: an untouched one is left out of the save (a fork inherits it).
  const [signatureHtml, setSignatureHtml] = useState<string | null>(null);
  // A stable id per step slot, independent of `steps`' array order. Without
  // this, `TemplateStepEditor` keyed by array index would get reused across
  // a duplicate/delete — React reconciles the same instance for a slot whose
  // *content* just shifted, and that instance's own local delay-picker state
  // (`wait`, seeded once on mount) goes stale for the new occupant. A stale
  // `wait.n` combined with a later unit change then silently writes the
  // wrong number into that step's `delayDays`.
  const [stepKeys, setStepKeys] = useState<string[]>(() => baseline.steps.map(() => crypto.randomUUID()));
  const [errors, setErrors] = useState<string[]>([]);
  const [serverError, setServerError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  // Until the admin types a name, a new template is named after its first
  // email's subject and goes up flagged as a default (the server then adds
  // " (2)" on a clash). A typed name that clashes is refused instead.
  const nameIsDefault = typedName === null && mode !== 'edit' && mode !== 'view';
  const name = typedName ?? (mode === 'create' ? defaultTemplateName(steps[0]?.subject ?? '') : baseline.name);

  // Derived, never stored: the owner remounts this editor with the saved row
  // after a successful save, so "differs from `initial`" is the honest test —
  // a save that was interrupted (e.g. the confirm-brand dialog was dismissed)
  // correctly leaves the form dirty.
  const dirty =
    mode === 'view'
      ? false
      : mode !== 'edit' || name !== baseline.name || signatureHtml !== null || archivedNos.length > 0 || !sameSteps(steps, baseline.steps);
  const shownWarnings = dirty ? [] : (lintWarnings ?? []);

  // Where the subject's merge-tag menu should splice the next tag. Captured on
  // every focus, click, keyup and change so a menu click (which blurs the
  // field) still knows the caret it left behind.
  const caretRef = useRef<{ index: number; field: TemplateStepField; start: number; end: number } | null>(null);

  const rememberCaret = (index: number, field: TemplateStepField, el: HTMLInputElement | HTMLTextAreaElement) => {
    const fallback = el.value.length;
    caretRef.current = { index, field, start: el.selectionStart ?? fallback, end: el.selectionEnd ?? fallback };
  };

  const patchStep = (index: number, patch: Partial<TemplateStepDraft>) => {
    setSteps((prev) => prev.map((step, i) => (i === index ? { ...step, ...patch } : step)));
  };

  const renumber = (drafts: TemplateStepDraft[]): TemplateStepDraft[] =>
    drafts.map((step, i) => ({ ...step, stepNo: i + 1 }));

  // The latest keys, so an answer that lands later finds its step by identity, not by a position that may have moved.
  const stepKeysRef = useRef(stepKeys);
  useEffect(() => {
    stepKeysRef.current = stepKeys;
  }, [stepKeys]);

  const atStepCap = steps.length >= MAX_STEPS;
  const manualSteps = useMemo(() => (mode === 'edit' || mode === 'view' ? (initial?.steps ?? []).filter((s) => s.manual) : []), [mode, initial]);

  const addFollowUp = () => {
    if (atStepCap) return;
    setSteps((prev) => [...prev, emptyFollowUpStep(prev.length + 1)]);
    setStepKeys((prev) => [...prev, crypto.randomUUID()]);
  };

  const insertStepAfter = (index: number, draft: TemplateStepDraft) => {
    if (atStepCap) return;
    caretRef.current = null;
    setSteps((prev) => renumber([...prev.slice(0, index + 1), draft, ...prev.slice(index + 1)]));
    setStepKeys((prev) => [...prev.slice(0, index + 1), crypto.randomUUID(), ...prev.slice(index + 1)]);
  };

  const removeStep = (index: number) => {
    caretRef.current = null;
    setSteps((prev) => renumber(prev.filter((_, i) => i !== index)));
    setStepKeys((prev) => prev.filter((_, i) => i !== index));
  };

  // Each step's body editor, by step key: a chip inserts at its cursor, keeping its formatting.
  const bodyHandles = useRef(new Map<string, ComposerEditorHandle>());
  const bodyHandleFor = (key: string) => (handle: ComposerEditorHandle | null) => {
    if (handle) bodyHandles.current.set(key, handle);
    else bodyHandles.current.delete(key);
  };
  // A sign-off removal rewrites a body as plain text: its editor re-reads it under a new key.
  const [bodySeeds, setBodySeeds] = useState<Record<string, number>>({});

  const insertTag = (index: number, field: TemplateStepField, tag: string) => {
    if (field === 'body') {
      bodyHandles.current.get(stepKeys[index] ?? '')?.insertTextAtCursor(tag);
      return;
    }
    const at = caretRef.current?.index === index && caretRef.current.field === 'subject' ? caretRef.current : null;
    setSteps((prev) =>
      prev.map((step, i) => {
        if (i !== index) return step;
        const start = at?.start ?? step.subject.length;
        const end = at?.end ?? step.subject.length;
        return { ...step, subject: `${step.subject.slice(0, start)}${tag}${step.subject.slice(end)}` };
      }),
    );
    const caret = (at?.start ?? steps[index]?.subject.length ?? 0) + tag.length;
    caretRef.current = { index, field: 'subject', start: caret, end: caret };
  };

  // The AI writes one step from the steps before it. A failure rejects and the step is left exactly as it was.
  const fillStep = async (index: number, instruction: string) => {
    const step = steps[index];
    if (!step || !onFillStep) return;
    const emptyBefore = steps.slice(0, index).findIndex((prior) => !prior.body.trim());
    if (emptyBefore >= 0) throw new PriorStepsEmptyError(emptyBefore + 2);
    const delay = Number(step.delayDays);
    const hasDelay = index > 0 && step.delayDays.trim() !== '' && Number.isFinite(delay) && delay >= 0 && delay <= MAX_DELAY_DAYS;
    const result = await onFillStep({
      stepNo: index + 1,
      stepsTotal: steps.length,
      ...(index === 0 && step.subject.trim() ? { subject: step.subject } : {}),
      ...(step.body.trim() ? { bodyHtml: bodyHtmlOf(step) } : {}),
      ...(instruction ? { instruction } : {}),
      ...(hasDelay ? { delayDays: delay } : {}),
      priorSteps: steps.slice(0, index).map((prior, i) => ({
        ...(i === 0 && prior.subject.trim() ? { subject: prior.subject } : {}),
        bodyHtml: bodyHtmlOf(prior),
      })),
    });
    const key = stepKeys[index] ?? '';
    const current = stepKeysRef.current.indexOf(key);
    if (current < 0) return;
    patchStep(current, {
      body: composerTextFromHtml(result.bodyHtml),
      bodyHtml: result.bodyHtml,
      aiAssisted: true,
      ...(current === 0 && result.subject ? { subject: result.subject } : {}),
    });
    setBodySeeds((prev) => ({ ...prev, [key]: (prev[key] ?? 0) + 1 }));
  };

  const buildInput = (): OutreachTemplateInput | OutreachTemplatePatch => {
    const payload = { name: name.trim(), steps: toTemplateSteps(steps) };
    // An untouched signature is omitted: a patch leaves it, a fork inherits the parent's. '' clears it.
    const signaturePart = signatureHtml === null ? {} : { signatureHtml };
    if (mode === 'edit') return { ...payload, ...signaturePart, ...(archivedNos.length > 0 ? { archiveSteps: archivedNos } : {}) };
    const defaultFlag = nameIsDefault ? { nameIsDefault: true } : {};
    if (mode === 'fork') {
      // Always a campaign template — see the module doc for why the scope
      // picker never shows here, regardless of what `scopeOptions` holds.
      return { ...payload, ...signaturePart, ...defaultFlag, scope: 'campaign', kind, parentTemplateId: initial?.id };
    }
    return { ...payload, ...(signatureHtml ? { signatureHtml } : {}), ...defaultFlag, scope, kind };
  };

  const handleSave = async () => {
    const found = validateTemplateDraft(name, steps, signature);
    setErrors(found);
    setServerError(null);
    setNameError(null);
    if (found.length > 0) return;
    setSaving(true);
    try {
      await onSave(buildInput(), false);
    } catch (err) {
      const message = saveErrorCopy(err);
      if (codeOf(err) === NAME_TAKEN) {
        setNameError(message);
      } else {
        setServerError(message);
        toast.error(message);
      }
    } finally {
      setSaving(false);
    }
  };

  // Preview needs a saved row: only edit and view have one before the first save.
  const previewTemplateId = (mode === 'edit' || mode === 'view') && initial ? initial.id : null;
  const locked = saving || readOnly;
  // Go refuses a first-mail subject over 60 and a follow-up body over 2,000; the first-touch body is advisory only.
  const overLimit = templateSubjectOverCap(steps[0]?.subject ?? '') || steps.slice(1).some((s) => bodyOverCap(s.body));
  const title = mode === 'view' ? 'View template' : mode === 'edit' ? 'Edit template' : 'New template';
  const messageCount = `${steps.length} ${steps.length === 1 ? 'message' : 'messages'}`;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            disabled={saving}
            onClick={onCancel}
            className="flex items-center gap-1.5 border-r border-border pr-3 text-body-sm-medium text-foreground hover:text-muted-foreground"
          >
            <ArrowLeft size={16} aria-hidden />
            <span>Templates</span>
          </button>
          <div>
            <h1 className="m-0 text-body-md-medium text-foreground">{title}</h1>
            <p className="m-0 text-caption-regular text-muted-foreground">
              Merge tags resolve per creator at send time
              {mode === 'fork' && initial ? ` · Duplicated from ${initial.name}` : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {readOnly ? (
            onDuplicate ? (
              <Button type="button" className="rounded-full" onClick={onDuplicate}>
                <Copy size={16} /> Duplicate into this campaign
              </Button>
            ) : null
          ) : (
            <>
              <Button type="button" variant="outline" className="rounded-full" disabled={saving} onClick={onCancel}>
                Cancel
              </Button>
              <Button type="button" className="rounded-full" loading={saving} disabled={overLimit} title={overLimit ? 'A subject or follow-up body is over its limit. Shorten it to save.' : undefined} onClick={() => void handleSave()}>
                <Check size={16} /> Save template
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid min-h-0 flex-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]">
        <div className="flex min-w-0 flex-col gap-4">
          <p className="max-w-3xl text-body-sm-regular text-muted-foreground">{TEMPLATE_HELP}</p>

          {errors.length > 0 ? (
            <ul className="flex flex-col gap-1 rounded-xl border border-rose-200 bg-rose-50 p-3">
              {errors.map((error) => (
                <li key={error} className="text-caption-regular text-rose-600">
                  {error}
                </li>
              ))}
            </ul>
          ) : null}

          {serverError ? (
            <p role="alert" className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-caption-regular text-rose-600">
              {serverError}
            </p>
          ) : null}

          <div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
            <div className="grid gap-3 md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_minmax(0,1fr)]">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={nameId}>Name</Label>
                <Input
                  id={nameId}
                  aria-label="Template name"
                  value={name}
                  disabled={locked}
                  aria-invalid={nameError ? true : undefined}
                  aria-describedby={nameError ? nameErrorId : undefined}
                  onChange={(e) => {
                    setTypedName(e.target.value);
                    setNameError(null);
                  }}
                />
                {nameError ? (
                  <p id={nameErrorId} role="alert" className="text-caption-regular text-rose-600">
                    {nameError}
                  </p>
                ) : null}
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={channelId}>Channel</Label>
                {/* Instagram DM is visible (Figma shows it as a real option) but
                    disabled — no per-template channel exists on the BE yet, same
                    "later phase" treatment as RowActions' disabled "Export sheet". */}
                <div className="relative">
                  <img src={gmailLogo} alt="" className="pointer-events-none absolute top-1/2 left-3 h-4 w-auto -translate-y-1/2" />
                  <select
                    id={channelId}
                    disabled
                    value="email"
                    title="Instagram DM — later phase"
                    className={`${SELECT_CLASS} pl-9`}
                  >
                    <option value="email">Email</option>
                    <option value="instagram" disabled>
                      Instagram DM
                    </option>
                  </select>
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={kindId}>Used for</Label>
                <select
                  id={kindId}
                  value={kind}
                  disabled={!kindOptions || locked}
                  title={kindOptions ? undefined : 'Set when the template is created'}
                  onChange={(e) => setKind(e.target.value as OutreachTemplateKind)}
                  className={SELECT_CLASS}
                >
                  {(kindOptions ?? [kind]).map((k) => (
                    <option key={k} value={k}>
                      {KIND_LABELS[k]}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {mode === 'create' && scopeOptions.length > 1 ? (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={scopeId}>Scope</Label>
                <select
                  id={scopeId}
                  value={scope}
                  disabled={saving}
                  onChange={(e) => setScope(e.target.value as TemplateEditorScope)}
                  className="h-9 w-fit rounded-md border border-border bg-background px-3 text-body-sm-regular"
                >
                  {scopeOptions.map((option) => (
                    <option key={option} value={option}>
                      {option === 'campaign' ? 'This campaign only' : 'All campaigns for this brand'}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            {steps.length === 1 ? (
              <p className="flex items-center gap-2 text-[12px] text-muted-foreground">
                <Info size={14} aria-hidden /> One message. Add a step below to turn it into a follow-up.
              </p>
            ) : null}
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{messageCount}</p>

            {shownWarnings.length > 0 ? (
              <p role="status" className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-caption-regular text-amber-800">
                {`Saved. ${shownWarnings.length} deliverability warning${shownWarnings.length === 1 ? '' : 's'} below — sending isn't blocked.`}
              </p>
            ) : null}

            {steps.map((step, index) => {
              const stepWarnings = shownWarnings.filter((w) => w.stepNo === index + 1);
              return (
                <Fragment key={stepKeys[index] ?? index}>
                  <TemplateStepEditor
                    index={index}
                    step={step}
                    disabled={saving}
                    readOnly={readOnly}
                    solo={steps.length === 1}
                    isLastStep={index === steps.length - 1 && steps.length >= 3}
                    onChange={(patch) => patchStep(index, patch)}
                    canDuplicate={!atStepCap}
                    onAiFill={onFillStep && !readOnly ? (instruction) => fillStep(index, instruction) : undefined}
                    onDuplicate={() => insertStepAfter(index, { ...step, stepNo: index + 2 })}
                    onDelete={index > 0 ? () => removeStep(index) : undefined}
                    onCaret={(field, el) => rememberCaret(index, field, el)}
                    onInsertTag={(field, tag) => insertTag(index, field, tag)}
                    bodyHandle={bodyHandleFor(stepKeys[index] ?? String(index))}
                    bodySeed={bodySeeds[stepKeys[index] ?? ''] ?? 0}
                    subjectHelp="Tidied once by AI for the whole campaign; tags like {creator_name} fill in per creator."
                    subjectWarning={index === 0 ? subjectWarningFor(step.subject, subjectContext) : undefined}
                    signOff={
                      signOffLineIndex(step.body) >= 0
                        ? {
                            message: SIGN_OFF_WARNING,
                            removes: signOffText(step.body),
                            onRemove: () => {
                              const bodyHtml = hasFormatting(step.bodyHtml) ? stripSignOffHtml(step.bodyHtml, step.body) : '';
                              // Owner 2026-10-12: an edit never drops formatting, so a sign-off that can't be cut cleanly stays.
                              if (bodyHtml === null) {
                                toast.message(SIGN_OFF_KEPT_FOR_FORMATTING);
                                return;
                              }
                              patchStep(index, { body: stripSignOff(step.body), bodyHtml });
                              const key = stepKeys[index] ?? '';
                              setBodySeeds((prev) => ({ ...prev, [key]: (prev[key] ?? 0) + 1 }));
                            },
                          }
                        : undefined
                    }
                  />
                  {stepWarnings.length > 0 ? (
                    <ul
                      aria-label={`Step ${index + 1} deliverability warnings`}
                      className="flex flex-col gap-1 rounded-xl border border-amber-200 bg-amber-50 p-3"
                    >
                      {stepWarnings.map((w, i) => (
                        <li key={`${w.code}-${i}`} className="text-caption-regular text-amber-800">
                          {lintLabel(w)}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </Fragment>
              );
            })}

            {manualSteps.length > 0 ? (
              <section aria-label="Manual follow-ups" className="flex flex-col gap-2">
                {manualSteps.map((step) => {
                  const archived = archivedNos.includes(step.stepNo);
                  return (
                    <div key={step.stepNo} className="flex flex-col gap-2 rounded-xl border border-border bg-card p-4">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-body-sm-medium text-foreground">Manual follow-up (not sent automatically)</p>
                        {readOnly ? null : (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={saving}
                            onClick={() => setArchivedNos((prev) => (archived ? prev.filter((n) => n !== step.stepNo) : [...prev, step.stepNo]))}
                          >
                            {archived ? 'Undo archive' : 'Archive'}
                          </Button>
                        )}
                      </div>
                      <p className={`whitespace-pre-wrap text-body-sm-regular ${archived ? 'text-muted-foreground line-through' : 'text-foreground'}`}>{step.body}</p>
                      {archived ? <p className="text-caption-regular text-muted-foreground">Archived when you save.</p> : null}
                    </div>
                  );
                })}
              </section>
            ) : null}

            {readOnly ? null : (
              <div className="flex flex-col items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-full"
                  disabled={saving || atStepCap}
                  onClick={addFollowUp}
                >
                  <Plus size={16} />
                  Add a follow-up
                </Button>
                {atStepCap ? <p className="text-caption-regular text-muted-foreground">{FOLLOW_UP_CAP_NOTE}</p> : null}
                <button type="button" disabled={saving} onClick={onCancel} className="text-body-sm-regular text-muted-foreground hover:text-foreground">
                  Exit
                </button>
              </div>
            )}

            <div className="flex flex-col gap-1.5 rounded-xl border border-border bg-card p-4">
              <p aria-hidden className="text-body-sm-medium text-foreground">
                Signature (optional)
              </p>
              <SignatureEditor
                ariaLabel="Signature (optional)"
                initialHtml={initial?.signatureHtml}
                initialText={initial?.signature ?? undefined}
                disabled={locked}
                help={TEMPLATE_SIGNATURE_HELP}
                onChange={(text, html) => {
                  setSignature(text);
                  setSignatureHtml(html);
                }}
              />
              {signatureWarnings(signature)
                .filter((w) => !shownWarnings.some((s) => s.stepNo === 0 && s.code === SIGNATURE_WARNING_CODES[w]))
                .map((w) => (
                  <p key={w} role="status" className="text-caption-regular text-amber-800">
                    {w}
                  </p>
                ))}
              {shownWarnings.some((w) => w.stepNo === 0) ? (
                <ul
                  aria-label="Signature deliverability warnings"
                  className="flex flex-col gap-1 rounded-xl border border-amber-200 bg-amber-50 p-3"
                >
                  {shownWarnings
                    .filter((w) => w.stepNo === 0)
                    .map((w, i) => (
                      <li key={`${w.code}-${i}`} className="text-caption-regular text-amber-800">
                        {lintLabel(w)}
                      </li>
                    ))}
                </ul>
              ) : null}
            </div>
          </div>
        </div>

        <TemplatePreviewPane
          templateId={previewTemplateId}
          entries={entries}
          preview={onPreview}
          dirty={dirty}
          steps={toTemplateSteps(steps)}
          signature={initial?.signature ?? null}
        />
      </div>
    </div>
  );
}
